// src/lib/data/strategies/CloudStrategy.js - V14 ENTERPRISE ULTRA-ROBUST
// ======================================================
// ☁️ CLOUD STRATEGY - V14 (GOOGLE/FACEBOOK/NETFLIX GRADE)
// ======================================================
// 🔥 V14: REAL-TIME SYNC — onSnapshot para propiedades, clientes, tareas
// 🔥 V14: TRANSACCIONES — runTransaction para updates concurrentes seguros
// 🔥 V14: SYNC STATUS — Notificaciones de sincronización entre dispositivos
// 🔥 V13: BUCLE-FIX — _checkExistingByTitle() NUNCA retorna IDs temporales
// 🔥 V13: BUCLE-FIX — create() no redirige a update() si existingByTitle.id es temporal
// 🔥 V13: AUTO-REPAIR con timeout — Si lock está tomado > 5s, fuerza release
// 🔥 V12: AUTO-REPAIR — update() con ID temporal → ejecuta create() transparente
// 🔥 V12: ANTI-DUPLICATE — create() verifica duplicados por título antes de insertar
// 🔥 V12: CLEANUP — _cleanupOrphanTempIds() limpia IDs huérfanos al iniciar
// ======================================================

import {
  getFirestore,
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  getDoc,
  setDoc,
  serverTimestamp,
  writeBatch,
  limit,
  onSnapshot,
  runTransaction
} from 'firebase/firestore';

import syncService from '../../firebase/syncService';
import { db as firebaseDb } from '../../firebase/config';

class CloudStrategy {
  constructor(user) {
    this.user = user;
    this.userId = user?.uid || null;
    this.companyId = user?.companyId || null;
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this.db = null;
    this.app = null;

    this._idCache = new Map();
    this._deletedCache = new Set();
    this._loadDeletedCacheFromStorage();

    // 🔥 V14: Listeners activos para real-time sync
    this._activeListeners = new Map();
    this._listenerCallbacks = new Map();

    this._autoRepairCount = 0;
    this._autoRepairLocks = new Map();

    this.initializeFirebase();

    if (!this.userId || !this.companyId) {
      console.error('❌ CLOUDSTRATEGY: Usuario sin ID o CompanyID:', {
        userId: this.userId, companyId: this.companyId, userObject: user
      });
      throw new Error('Usuario inválido para CloudStrategy: falta userId o companyId');
    }

    this._localStrategy = null;

    console.log(`✅ CloudStrategy V14 inicializado para usuario ${this.userId} en empresa ${this.companyId}`);
    console.log(`📍 Ruta de datos: companies/${this.companyId}/`);
    console.log(`🔄 V14: Real-time Sync: ACTIVADO (propiedades, clientes, tareas)`);
    console.log(`🔒 V14: Transacciones: ACTIVADO (runTransaction)`);
    console.log(`🪦 Tombstone Integration: ACTIVADO`);
    console.log(`🔧 V13: Auto-Repair IDs temporales: ACTIVADO (con bucle-fix)`);
    console.log(`🔧 V13: Anti-Duplicate por título: ACTIVADO (con bucle-fix)`);
    console.log(`🔄 Modo: ${this.isOnline ? 'ONLINE (Firestore + SyncService + Real-time)' : 'OFFLINE (SyncQueue)'}`);

    // Bind de métodos
    this.setCompanyId = this.setCompanyId.bind(this);
    this.getCompanyId = this.getCompanyId.bind(this);
    this.getCollectionRef = this.getCollectionRef.bind(this);
    this.getDocRef = this.getDocRef.bind(this);
    this.create = this.create.bind(this);
    this.read = this.read.bind(this);
    this.update = this.update.bind(this);
    this.delete = this.delete.bind(this);
    this.save = this.save.bind(this);
    this.getAll = this.getAll.bind(this);
    this.verifyDocumentOwnership = this.verifyDocumentOwnership.bind(this);
    this.getDashboardStats = this.getDashboardStats.bind(this);
    this.getTasksByClient = this.getTasksByClient.bind(this);
    this.getCalendarEventsByProperty = this.getCalendarEventsByProperty.bind(this);
    this.healthCheck = this.healthCheck.bind(this);
    this.verifyPermissions = this.verifyPermissions.bind(this);
    this.getSyncStatus = this.getSyncStatus.bind(this);
    this.forceSyncNow = this.forceSyncNow.bind(this);
    this._writeToLocalStorage = this._writeToLocalStorage.bind(this);
    this._removeFromLocalStorage = this._removeFromLocalStorage.bind(this);
    this._updateLocalId = this._updateLocalId.bind(this);
    this._markAsDeleted = this._markAsDeleted.bind(this);
    this._isMarkedAsDeleted = this._isMarkedAsDeleted.bind(this);
    this._publishToPublicProperties = this._publishToPublicProperties.bind(this);
    this._createTombstoneInLocal = this._createTombstoneInLocal.bind(this);
    this._isTombstonedInLocal = this._isTombstonedInLocal.bind(this);
    this._loadDeletedCacheFromStorage = this._loadDeletedCacheFromStorage.bind(this);
    this._saveDeletedCacheToStorage = this._saveDeletedCacheToStorage.bind(this);
    this._checkExistingByTitle = this._checkExistingByTitle.bind(this);
    this._cleanupOrphanTempIds = this._cleanupOrphanTempIds.bind(this);
    this._acquireAutoRepairLock = this._acquireAutoRepairLock.bind(this);
    this._releaseAutoRepairLock = this._releaseAutoRepairLock.bind(this);
    // 🔥 V14: Nuevos métodos
    this.subscribeToCollection = this.subscribeToCollection.bind(this);
    this.unsubscribeFromCollection = this.unsubscribeFromCollection.bind(this);
    this.unsubscribeAll = this.unsubscribeAll.bind(this);
    this.updateWithTransaction = this.updateWithTransaction.bind(this);
  }

  // ======================================================
  // 🔥 V14: REAL-TIME SYNC — LISTENERS
  // ======================================================
  
  /**
   * Suscribirse a cambios en tiempo real de una colección
   * @param {string} collectionName - properties, clients, tasks, etc.
   * @param {function} callback - (data, changes) => void
   * @returns {function} unsubscribe
   */
  subscribeToCollection(collectionName, callback) {
    if (!this.db || !this.isOnline) {
      console.warn(`⚠️ [V14] No se puede suscribir a ${collectionName}: offline`);
      return () => {};
    }

    const listenerKey = `${collectionName}_${this.companyId}`;
    
    // Si ya existe un listener, desuscribir primero
    if (this._activeListeners.has(listenerKey)) {
      this.unsubscribeFromCollection(collectionName);
    }

    try {
      const colRef = this.getCollectionRef(collectionName);
      const q = query(
        colRef,
        where('companyId', '==', this.companyId),
        orderBy('updatedAt', 'desc')
      );

      console.log(`🔄 [V14] Suscribiendo a cambios en: ${collectionName}`);

      const unsubscribe = onSnapshot(q, 
        (snapshot) => {
          const changes = snapshot.docChanges().map(change => ({
            type: change.type, // 'added', 'modified', 'removed'
            id: change.doc.id,
            data: change.doc.data(),
            oldIndex: change.oldIndex,
            newIndex: change.newIndex
          }));

          const data = snapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(item => !this._deletedCache.has(`${collectionName}:${item.id}`));

          // Notificar cambios para sincronización entre dispositivos
          if (changes.length > 0) {
            window.dispatchEvent(new CustomEvent('cloud:dataChanged', {
              detail: { collection: collectionName, changes, timestamp: new Date().toISOString() }
            }));
          }

          callback(data, changes);
        },
        (error) => {
          console.error(`❌ [V14] Error en listener de ${collectionName}:`, error.message);
          // Reintentar suscripción después de 5 segundos
          setTimeout(() => {
            if (this.isOnline && this.db) {
              console.log(`🔄 [V14] Reintentando suscripción a ${collectionName}...`);
              this.subscribeToCollection(collectionName, callback);
            }
          }, 5000);
        }
      );

      this._activeListeners.set(listenerKey, unsubscribe);
      this._listenerCallbacks.set(listenerKey, callback);
      
      console.log(`✅ [V14] Suscrito a ${collectionName} — cambios en tiempo real activos`);
      return unsubscribe;
    } catch (error) {
      console.error(`❌ [V14] Error al suscribir a ${collectionName}:`, error.message);
      return () => {};
    }
  }

  /**
   * Desuscribirse de una colección
   */
  unsubscribeFromCollection(collectionName) {
    const listenerKey = `${collectionName}_${this.companyId}`;
    const unsubscribe = this._activeListeners.get(listenerKey);
    if (unsubscribe) {
      unsubscribe();
      this._activeListeners.delete(listenerKey);
      this._listenerCallbacks.delete(listenerKey);
      console.log(`🔌 [V14] Desuscrito de ${collectionName}`);
    }
  }

  /**
   * Desuscribirse de todas las colecciones
   */
  unsubscribeAll() {
    this._activeListeners.forEach((unsubscribe, key) => {
      unsubscribe();
      console.log(`🔌 [V14] Desuscrito de ${key}`);
    });
    this._activeListeners.clear();
    this._listenerCallbacks.clear();
  }

  // ======================================================
  // 🔥 V14: TRANSACCIONES SEGURAS
  // ======================================================
  
  /**
   * Actualizar un documento con transacción (evita escrituras concurrentes)
   * @param {string} collectionName 
   * @param {string} id 
   * @param {function} updateFn - (currentData) => newData
   */
  async updateWithTransaction(collectionName, id, updateFn) {
    if (!this.db || !this.isOnline) {
      throw new Error('Transacciones requieren conexión a Firestore');
    }

    try {
      const docRef = this.getDocRef(collectionName, id);
      
      const result = await runTransaction(this.db, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (!docSnap.exists()) {
          throw new Error(`Documento ${collectionName}/${id} no encontrado`);
        }

        const currentData = docSnap.data();
        
        // Verificar que pertenece a la compañía
        if (currentData.companyId !== this.companyId) {
          throw new Error('No autorizado: el documento pertenece a otra compañía');
        }

        // Aplicar la función de actualización
        const updatedData = updateFn(currentData);
        
        if (!updatedData || typeof updatedData !== 'object') {
          throw new Error('updateFn debe retornar un objeto con los datos actualizados');
        }

        // Escribir en la transacción
        transaction.update(docRef, {
          ...updatedData,
          updatedAt: new Date().toISOString(),
          updatedBy: this.userId,
          _transactionId: `${Date.now()}_${Math.random().toString(36).substr(2, 6)}`
        });

        return { id, ...updatedData };
      });

      console.log(`🔒 [V14] Transacción exitosa en ${collectionName}/${id}`);
      
      // Publicar en propiedades públicas si aplica
      if (collectionName === 'properties') {
        await this._publishToPublicProperties(result, id);
      }

      // Actualizar localStorage
      if (!this._localStrategy) {
        await this._writeToLocalStorage(collectionName, result);
      }

      return { ...result, _syncStatus: 'synced', _transactional: true };
    } catch (error) {
      console.error(`❌ [V14] Error en transacción ${collectionName}/${id}:`, error.message);
      throw error;
    }
  }

  // ======================================================
  // 🔥 V13: SISTEMA DE LOCKS MEJORADO
  // ======================================================
  _acquireAutoRepairLock(id) {
    const key = String(id);
    const existing = this._autoRepairLocks.get(key);
    if (existing && (Date.now() - existing) > 5000) {
      console.warn(`⚠️ [V13] Lock stale detectado para "${key}", liberando...`);
      this._autoRepairLocks.delete(key);
    }
    if (this._autoRepairLocks.has(key)) return false;
    this._autoRepairLocks.set(key, Date.now());
    return true;
  }

  _releaseAutoRepairLock(id) {
    this._autoRepairLocks.delete(String(id));
  }

  // ======================================================
  // 🔥 V12: LIMPIEZA DE IDs HUÉRFANOS
  // ======================================================
  async _cleanupOrphanTempIds() {
    try {
      console.log('🧹 [V13] Iniciando limpieza de IDs huérfanos...');
      let cleanedCount = 0;
      if (!this._localStrategy) {
        console.log('⏭️ [V13] Sin LocalStrategy, omitiendo limpieza de huérfanos');
        return;
      }
      const collections = ['properties', 'clients', 'tasks', 'calendarEvents', 'documents', 'messages', 'financeiro'];
      for (const col of collections) {
        try {
          const localData = await this._localStrategy.read(col, {}, {});
          if (!Array.isArray(localData)) continue;
          const orphanIds = localData.filter(item => {
            const id = String(item?.id || '');
            return this._isTempId(id);
          });
          for (const orphan of orphanIds) {
            const orphanId = String(orphan.id);
            try {
              if (this.db && this.isOnline) {
                const existing = await this._getExisting(col, orphanId);
                if (!existing) {
                  console.log(`🪦 [V13 CLEANUP] Eliminando huérfano: ${col}/${orphanId}`);
                  await this._createTombstoneInLocal(col, orphanId);
                  await this._localStrategy.delete(col, orphanId);
                  cleanedCount++;
                }
              }
            } catch (e) {
              console.warn(`⚠️ [V13] Error limpiando huérfano ${col}/${orphanId}:`, e.message);
            }
          }
        } catch (e) {}
      }
      if (cleanedCount > 0) {
        console.log(`✅ [V13] Limpieza completada: ${cleanedCount} huérfanos eliminados`);
      } else {
        console.log(`✅ [V13] No se encontraron huérfanos - sistema limpio`);
      }
    } catch (error) {
      console.warn('⚠️ [V13] Error en limpieza de huérfanos (no crítico):', error.message);
    }
  }

  // ======================================================
  // 🔥 V13: VERIFICACIÓN DE DUPLICADOS POR TÍTULO
  // ======================================================
  async _checkExistingByTitle(collectionName, data) {
    if (!this.db || !this.isOnline) return null;
    
    const titleFields = {
      'properties': 'title',
      'clients': 'name',
      'tasks': 'title',
      'documents': 'name',
      'calendarEvents': 'title'
    };

    const titleField = titleFields[collectionName];
    if (!titleField || !data[titleField]) return null;

    try {
      const colRef = this.getCollectionRef(collectionName);
      const q = query(
        colRef,
        where('companyId', '==', this.companyId),
        where(titleField, '==', data[titleField]),
        limit(3)
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const matches = [];
        snapshot.forEach(doc => {
          const docData = doc.data();
          if (docData.companyId === this.companyId) {
            matches.push({ id: doc.id, ...docData });
          }
        });

        if (matches.length > 0) {
          const validMatches = matches.filter(m => !this._isTempId(m.id));
          
          if (validMatches.length > 0) {
            console.log(`🔍 [V13] Encontrado(s) ${validMatches.length} duplicado(s) REAL(es) por título en ${collectionName}: "${data[titleField]}"`);
            const sorted = validMatches.sort((a, b) => {
              const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime();
              const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime();
              return dateB - dateA;
            });
            return sorted[0];
          }
          
          console.log(`⏭️ [V13] Todos los duplicados de "${data[titleField]}" en ${collectionName} son IDs temporales [${matches.map(m => m.id).join(', ')}] — creando nuevo documento`);
          return null;
        }
      }
    } catch (error) {
      console.warn(`⚠️ [V13] Error verificando duplicados por título en ${collectionName}:`, error.message);
    }
    return null;
  }

  // ======================================================
  // 🔥 V11: INTEGRACIÓN CON LOCALSTRATEGY
  // ======================================================
  setLocalStrategy(localStrategy) {
    this._localStrategy = localStrategy;
    console.log('🔗 CloudStrategy V14: LocalStrategy vinculada — localStorage delegado a DualSync');
  }

  async _createTombstoneInLocal(collectionName, id) {
    try {
      if (this._localStrategy && typeof this._localStrategy.createTombstone === 'function') {
        await this._localStrategy.createTombstone(collectionName, id, {
          userId: this.userId, deletedBy: this.userId, reason: 'cloud_delete', _version: 1
        });
        return true;
      }
      this._createTombstoneFallback(collectionName, id);
      return true;
    } catch (error) {
      console.warn('⚠️ [V13] Error creando tombstone:', error.message);
      this._createTombstoneFallback(collectionName, id);
      return false;
    }
  }

  _createTombstoneFallback(collectionName, id) {
    try {
      const dbKey = `imoveispro_db_user_${this.userId}`;
      const db = JSON.parse(localStorage.getItem(dbKey) || '{}');
      if (!Array.isArray(db._tombstones)) db._tombstones = [];
      const exists = db._tombstones.some(t => t.collection === collectionName && t.recordId === String(id));
      if (!exists) {
        db._tombstones.push({
          id: `tombstone_${collectionName}_${id}_${Date.now()}`,
          collection: collectionName, recordId: String(id),
          deletedAt: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          userId: this.userId, deletedBy: this.userId,
          reason: 'cloud_delete_fallback', _version: 1, _tombstoneVersion: 'V13'
        });
        localStorage.setItem(dbKey, JSON.stringify(db));
      }
    } catch (error) {
      console.warn('⚠️ [V13] Error en fallback de tombstone:', error.message);
    }
  }

  async _isTombstonedInLocal(collectionName, id) {
    try {
      if (this._localStrategy && typeof this._localStrategy.isTombstoned === 'function') {
        return await this._localStrategy.isTombstoned(collectionName, id);
      }
      const dbKey = `imoveispro_db_user_${this.userId}`;
      const db = JSON.parse(localStorage.getItem(dbKey) || '{}');
      const tombstones = db._tombstones || [];
      const now = new Date();
      return tombstones.some(t => 
        t.collection === collectionName && t.recordId === String(id) &&
        (!t.expiresAt || new Date(t.expiresAt) >= now)
      );
    } catch (error) { return false; }
  }

  // ======================================================
  // 🔥 CACHE DE ELIMINADOS PERSISTENTE
  // ======================================================
  _loadDeletedCacheFromStorage() {
    try {
      const stored = sessionStorage.getItem(`cloud_deleted_cache_${this.userId}`);
      if (stored) { const items = JSON.parse(stored); this._deletedCache = new Set(items); }
    } catch (error) {}
  }

  _saveDeletedCacheToStorage() {
    try {
      const items = Array.from(this._deletedCache);
      sessionStorage.setItem(`cloud_deleted_cache_${this.userId}`, JSON.stringify(items.slice(-500)));
    } catch (error) {}
  }

  // ======================================================
  // 🔐 VERIFICACIÓN DE PERMISOS
  // ======================================================
  verifyPermissions(operation = 'read') {
    if (!this.user || !this.user.uid) throw new Error('Usuario no autenticado');
    if (!this.companyId) throw new Error('Compañía no especificada');
    const isAdmin = this.user.role === 'admin' || this.user._securityLevel === 'admin' || this.user.role === 'owner';
    if (isAdmin) return true;
    if (operation === 'write' && this.user.canWrite === false) throw new Error('No tienes permisos de escritura');
    if (operation === "delete" && this.user.canDelete === false && this.user.role !== "admin") throw new Error('No tienes permisos de eliminación');
    return true;
  }

  // ======================================================
  // 🔥 INICIALIZACIÓN DE FIREBASE
  // ======================================================
  initializeFirebase() {
    try {
      this.db = firebaseDb?._isDummy ? null : firebaseDb;
      this.app = this.db?.app || null;
      if (!this.db) throw new Error('Firebase db não disponível');
      syncService.initialize();
    } catch (error) {
      console.error('❌ Error inicializando Firebase:', error);
      this.db = null;
      throw error;
    }
  }

  // ======================================================
  // 🏢 GESTIÓN DE COMPANY ID
  // ======================================================
  setCompanyId(companyId) { this.companyId = companyId; }
  getCompanyId() { if (!this.companyId) throw new Error('Company ID no definido en CloudStrategy'); return this.companyId; }
  getCurrentPath() { return `companies/${this.getCompanyId()}/`; }

  // ======================================================
  // 📁 REFERENCIAS A COLECCIONES Y DOCUMENTOS
  // ======================================================
  _isGlobalCollection(collectionName) {
    return ['users', 'companies', 'public_properties', 'public_companies', 'public'].includes(collectionName);
  }

  getCollectionRef(collectionName) {
    if (!this.db) throw new Error('Firebase no inicializado');
    const companyId = this.getCompanyId();
    return this._isGlobalCollection(collectionName)
      ? collection(this.db, collectionName)
      : collection(this.db, `companies/${companyId}/${collectionName}`);
  }

  getDocRef(collectionName, id) {
    if (!this.db) throw new Error('Firebase no inicializado');
    const companyId = this.getCompanyId();
    return this._isGlobalCollection(collectionName)
      ? doc(this.db, collectionName, id)
      : doc(this.db, `companies/${companyId}/${collectionName}`, id);
  }

  // ======================================================
  // ✅ HELPERS INTERNOS
  // ======================================================
  _cleanData(data) {
    const clean = { ...data };
    Object.keys(clean).forEach(key => { if (clean[key] === undefined) clean[key] = null; });
    return clean;
  }
  
  _isTempId(id) {
    const str = String(id);
    return str.startsWith('temp-') || str.startsWith('temp_') || str.startsWith('offline_');
  }

  resolveId(localId) {
    if (!localId) return localId;
    const resolved = this._idCache.get(String(localId));
    return resolved || localId;
  }

  _registerIdMapping(localId, firestoreId, syncManagerRef = null) {
    if (localId && firestoreId && String(localId) !== String(firestoreId)) {
      this._idCache.set(String(localId), String(firestoreId));
      if (syncManagerRef && typeof syncManagerRef.registerIdMapping === 'function') {
        syncManagerRef.registerIdMapping(localId, firestoreId);
      }
    }
  }
  
  _markAsDeleted(collectionName, id) {
    this._deletedCache.add(`${collectionName}:${id}`);
    this._saveDeletedCacheToStorage();
  }
  
  async _isMarkedAsDeleted(collectionName, id) {
    const key = `${collectionName}:${id}`;
    if (this._deletedCache.has(key)) return true;
    const isTombstoned = await this._isTombstonedInLocal(collectionName, id);
    if (isTombstoned) { this._deletedCache.add(key); return true; }
    return false;
  }

  async _getExisting(collectionName, id) {
    try {
      if (!id || this._isTempId(id)) return null;
      if (await this._isMarkedAsDeleted(collectionName, id)) return null;
      const docRef = this.getDocRef(collectionName, id);
      const snap = await getDoc(docRef);
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    } catch (error) { return null; }
  }

  _mergeWithExisting(newData, existingData, collectionName) {
    if (!existingData) return newData;
    const merged = { ...newData };
    if (collectionName === 'properties') {
      if (!newData.images || (Array.isArray(newData.images) && newData.images.length === 0)) {
        merged.images = existingData.images || [];
      }
      if (!merged.createdAt && existingData.createdAt) merged.createdAt = existingData.createdAt;
      if (!merged.createdBy && existingData.createdBy) merged.createdBy = existingData.createdBy;
    }
    return merged;
  }

  // ======================================================
  // 🌐 PUBLICACIÓN AUTOMÁTICA
  // ======================================================
  async _publishToPublicProperties(propertyData, firestoreId) {
    if (!this.db || !this.isOnline || !firestoreId) return;
    try {
      const now = new Date().toISOString();
      const publicRef = doc(this.db, 'public_properties', firestoreId);
      await setDoc(publicRef, {
        id: firestoreId,
        title: propertyData.title || 'Imóvel sem título',
        description: propertyData.description || '',
        price: propertyData.price || null,
        type: propertyData.type || null,
        status: propertyData.status || 'Disponível',
        address: propertyData.address || '',
        city: propertyData.city || '',
        neighborhood: propertyData.neighborhood || '',
        bedrooms: propertyData.bedrooms || 0,
        bathrooms: propertyData.bathrooms || 0,
        area: propertyData.area || 0,
        images: propertyData.images || [],
        featured: propertyData.featured || false,
        companyId: this.companyId,
        userId: this.userId,
        ownerId: propertyData.ownerId || this.userId,
        createdBy: propertyData.createdBy || this.userId,
        ownerName: propertyData.ownerName || this.user?.name || null,
        ownerEmail: propertyData.ownerEmail || this.user?.email || null,
        isPublic: true,
        publishedAt: propertyData.publishedAt || now,
        updatedAt: now,
        company: propertyData.company || null
      }, { merge: true });
    } catch (error) {
      console.warn(`⚠️ [V9] Error publicando en public_properties (no crítico):`, error.message);
    }
  }

  // ======================================================
  // 🔥 V13: CREATE — BLINDADO
  // ======================================================
  async create(collectionName, data) {
    this.verifyPermissions('write');

    try {
      let cleanData = this._cleanData(data);
      if (collectionName === 'properties' && cleanData.images === undefined) {
        cleanData.images = [];
      }

      const now = new Date().toISOString();
      const localId = data.id || data._localId || this._generateOfflineId();
      const isDeleted = await this._isMarkedAsDeleted(collectionName, localId);
      const effectiveId = isDeleted ? this._generateOfflineId() : localId;
      
      if (isDeleted) {
        console.log(`🪦 [V13] ID ${localId} estaba eliminado, usando nuevo ID: ${effectiveId}`);
      }

      if (this.db && this.isOnline) {
        const existingByTitle = await this._checkExistingByTitle(collectionName, cleanData);
        if (existingByTitle) {
          if (this._isTempId(existingByTitle.id)) {
            console.log(`⏭️ [V13] ID existente "${existingByTitle.id}" es temporal — creando nuevo documento en vez de redirigir`);
          } else {
            console.log(`🔄 [V13 ANTI-DUPLICATE] "${cleanData.title || cleanData.name || 'item'}" ya existe como ${existingByTitle.id} — actualizando`);
            return this.update(collectionName, existingByTitle.id, cleanData);
          }
        }
      }

      const itemWithContext = {
        ...cleanData,
        id: effectiveId,
        companyId: this.companyId,
        createdBy: this.userId,
        createdAt: cleanData.createdAt || now,
        updatedAt: now,
        updatedBy: this.userId,
        _localId: effectiveId,
        _syncStatus: 'pending'
      };

      if (!this._localStrategy) {
        this._writeToLocalStorage(collectionName, itemWithContext);
      }

      let firestoreId = null;

      if (this.db && this.isOnline) {
        try {
          if (await this._isMarkedAsDeleted(collectionName, effectiveId)) {
            return {
              id: effectiveId, ...itemWithContext,
              _syncStatus: 'cancelled', _cancelledReason: 'tombstoned', _offline: true
            };
          }
          
          if (this._isTempId(effectiveId)) {
            const colRef = this.getCollectionRef(collectionName);
            const newDocRef = await addDoc(colRef, {
              ...itemWithContext,
              _syncStatus: 'synced',
              _syncedAt: now
            });
            firestoreId = newDocRef.id;
            this._registerIdMapping(effectiveId, firestoreId);
            if (this._localStrategy) {
              await this._localStrategy.update(collectionName, effectiveId, {
                ...itemWithContext, id: firestoreId, _firestoreId: firestoreId, _syncStatus: 'synced'
              }).catch(() => {});
            }
            if (!this._localStrategy) {
              this._updateLocalId(collectionName, effectiveId, firestoreId);
              this._removeFromLocalStorage(collectionName, effectiveId);
            }
            console.log(`🔄 [V14] ID temporal ${effectiveId} → Firestore ID real: ${firestoreId}`);
          } else {
            const existingDoc = await this._getExisting(collectionName, effectiveId);
            const docRef = this.getDocRef(collectionName, effectiveId);
            
            if (existingDoc) {
              const mergedData = this._mergeWithExisting(itemWithContext, existingDoc, collectionName);
              await updateDoc(docRef, { ...mergedData, updatedAt: now, updatedBy: this.userId, _syncStatus: 'synced', _syncedAt: now });
              firestoreId = effectiveId;
              if (!this._localStrategy) this._updateLocalId(collectionName, effectiveId, firestoreId);
            } else {
              await setDoc(docRef, { ...itemWithContext, _syncStatus: 'synced', _syncedAt: now });
              firestoreId = effectiveId;
              if (!this._localStrategy) this._updateLocalId(collectionName, effectiveId, firestoreId);
              if (effectiveId !== firestoreId) this._registerIdMapping(effectiveId, firestoreId);
            }
          }
          
          if (collectionName === 'properties' && firestoreId) {
            await this._publishToPublicProperties(itemWithContext, firestoreId);
          } 
        } catch (firestoreError) {
          console.warn(`⚠️ Firestore write falló para ${collectionName}:`, firestoreError.message);
          try { await syncService.syncCreate(collectionName, itemWithContext, this.companyId, this.userId); } catch (syncError) {}
        }
      } else {
        try {
          await syncService.syncCreate(collectionName, itemWithContext, this.companyId, this.userId);
        } catch (syncError) {}
      }

      return {
        id: firestoreId || effectiveId,
        ...itemWithContext,
        _firestoreId: firestoreId || null,
        _localId: effectiveId,
        _syncStatus: firestoreId ? 'synced' : 'pending',
        _offline: !firestoreId
      };
    } catch (error) {
      console.error(`❌ Error creando ${collectionName}:`, error);
      throw error;
    }
  }

  // ======================================================
  // 🔧 HELPERS DE LOCALSTORAGE
  // ======================================================
  async _writeToLocalStorage(collectionName, item) {
    try {
      if (await this._isMarkedAsDeleted(collectionName, item.id || item._localId)) return;
      const dbKey = `imoveispro_db_user_${this.userId}`;
      const db = JSON.parse(localStorage.getItem(dbKey) || '{}');
      if (!db[collectionName]) db[collectionName] = [];
      const existingIndex = db[collectionName].findIndex(
        existing => existing.id === item.id || existing._localId === item._localId || existing._firestoreId === item.id
      );
      if (existingIndex >= 0) { db[collectionName][existingIndex] = item; }
      else { db[collectionName].unshift(item); }
      localStorage.setItem(dbKey, JSON.stringify(db));
    } catch (error) {}
  }
  
  _removeFromLocalStorage(collectionName, id) {
    try {
      const dbKey = `imoveispro_db_user_${this.userId}`;
      const db = JSON.parse(localStorage.getItem(dbKey) || '{}');
      if (db[collectionName]) {
        db[collectionName] = db[collectionName].filter(
          item => item.id !== id && item._localId !== id && item._firestoreId !== id
        );
        localStorage.setItem(dbKey, JSON.stringify(db));
      }
    } catch (error) {}
  }

  _updateLocalId(collectionName, localId, firestoreId) {
    if (localId === firestoreId) return;
    try {
      const dbKey = `imoveispro_db_user_${this.userId}`;
      const db = JSON.parse(localStorage.getItem(dbKey) || '{}');
      if (db[collectionName]) {
        const index = db[collectionName].findIndex(item => item.id === localId || item._localId === localId);
        if (index >= 0) {
          db[collectionName][index].id = firestoreId;
          db[collectionName][index]._firestoreId = firestoreId;
          db[collectionName][index]._syncStatus = 'synced';
          localStorage.setItem(dbKey, JSON.stringify(db));
        }
      }
    } catch (error) {}
  }

  // ======================================================
  // 📖 READ
  // ======================================================
  async read(collectionName, queryOptions = {}) {
    this.verifyPermissions('read');
    if (!this.db) throw new Error('Firebase no inicializado');
    try {
      const collectionRef = this.getCollectionRef(collectionName);
      let q;
      if (this._isGlobalCollection(collectionName)) {
        q = query(collectionRef);
      } else {
        q = query(collectionRef, where('companyId', '==', this.companyId));
      }
      if (queryOptions.where && Array.isArray(queryOptions.where)) {
        queryOptions.where.forEach(filter => {
          if (filter.value !== undefined && filter.value !== null) {
            q = query(q, where(filter.field, filter.operator, filter.value));
          }
        });
      }
      if (queryOptions.orderBy) {
        const orderField = queryOptions.orderBy.field || queryOptions.orderBy;
        const direction = queryOptions.orderBy.direction || 'asc';
        if (orderField) q = query(q, orderBy(orderField, direction));
      }
      const snapshot = await getDocs(q);
      const results = [];
      for (const docSnap of snapshot.docs) {
        const docData = docSnap.data();
        const clean = { ...docData };
        if (clean.createdAt?.toDate) clean.createdAt = clean.createdAt.toDate().toISOString();
        if (clean.updatedAt?.toDate) clean.updatedAt = clean.updatedAt.toDate().toISOString();
        if (!(await this._isMarkedAsDeleted(collectionName, docSnap.id))) {
          results.push({ id: docSnap.id, ...clean });
        }
      }
      return [...new Map(results.map(r => [r.id, r])).values()];
    } catch (error) {
      if (error.code === 'failed-precondition') return [];
      throw error;
    }
  }

  // ======================================================
  // ✏️ UPDATE — V13 AUTO-REPAIR
  // ======================================================
  async update(collectionName, id, data) {
    this.verifyPermissions('write');

    try {
      const resolvedId = this.resolveId(id);
      if (resolvedId !== id) {
        console.log(`🗺️ update() resolvió ID: ${id} → ${resolvedId}`);
      }

      if (this._isTempId(resolvedId)) {
        if (!this._acquireAutoRepairLock(resolvedId)) {
          console.warn(`⏳ [V13] Auto-repair ya en progreso para "${resolvedId}", esperando...`);
          await new Promise(r => setTimeout(r, 500));
          const newResolved = this.resolveId(id);
          if (!this._isTempId(newResolved)) {
            return this.update(collectionName, newResolved, data);
          }
          console.warn(`⚠️ [V13] Lock persistente para "${resolvedId}" — forzando create directo`);
        }

        try {
          this._autoRepairCount++;
          console.warn(`🔄 [V13 AUTO-REPAIR #${this._autoRepairCount}] ID temporal "${resolvedId}" → ejecutando CREATE`);
          
          const repairedData = { 
            ...data, 
            _repairedFrom: id, 
            _autoRepairedAt: new Date().toISOString(),
            _repairReason: 'temp_id_update_rejected',
            _repairCount: this._autoRepairCount
          };
          delete repairedData.id;
          
          const result = await this.create(collectionName, repairedData);
          console.log(`✅ [V13 AUTO-REPAIR] Reparación exitosa: ${id} → ${result.id}`);
          return result;
        } finally {
          this._releaseAutoRepairLock(resolvedId);
        }
      }
      
      if (await this._isMarkedAsDeleted(collectionName, resolvedId)) {
        throw new Error(`update() rechazado: documento ${collectionName}/${resolvedId} fue eliminado.`);
      }

      const existingData = await this._getExisting(collectionName, resolvedId);
      if (!existingData && this.isOnline) {
        console.warn(`⚠️ update() - documento ${resolvedId} no existe en Firestore, creando...`);
        return this.create(collectionName, { ...data, id: resolvedId });
      }

      const cleanData = this._cleanData(data);
      const mergedData = this._mergeWithExisting(cleanData, existingData, collectionName);
      const updatePayload = { ...mergedData, updatedAt: new Date().toISOString(), updatedBy: this.userId };

      if (this.db && this.isOnline) {
        const docRef = this.getDocRef(collectionName, resolvedId);
        await updateDoc(docRef, updatePayload);
        if (!this._localStrategy) {
          await this._writeToLocalStorage(collectionName, { id: resolvedId, ...updatePayload, _syncStatus: 'synced' });
        }
        if (collectionName === 'properties') {
          await this._publishToPublicProperties(updatePayload, resolvedId);
        }
        return { id: resolvedId, ...updatePayload, _syncStatus: 'synced' };
      }

      const syncId = await syncService.syncUpdate(collectionName, resolvedId, updatePayload, this.companyId, this.userId);
      if (!this._localStrategy) {
        await this._writeToLocalStorage(collectionName, { id: resolvedId, ...updatePayload, _syncStatus: 'pending' });
      }
      return { id: resolvedId, ...updatePayload, _syncId: syncId, _syncStatus: 'pending', _offline: true };
    } catch (error) {
      console.error(`❌ Error actualizando ${collectionName}/${id}:`, error);
      throw error;
    }
  }

  // ======================================================
  // 💾 SAVE
  // ======================================================
  async save(collectionName, data, id = null) {
    try {
      this.verifyPermissions('write');
      if (!data || typeof data !== 'object') throw new Error('Datos inválidos para guardar');
      const resolvedId = id ? this.resolveId(id) : null;
      if (resolvedId && !this._isTempId(resolvedId) && !(await this._isMarkedAsDeleted(collectionName, resolvedId))) {
        return this.update(collectionName, resolvedId, data);
      }
      return this.create(collectionName, data);
    } catch (error) {
      console.error(`❌ Error en CloudStrategy.save(${collectionName}):`, error);
      throw error;
    }
  }

  // ======================================================
  // 🗑️ DELETE
  // ======================================================
  async delete(collectionName, id) {
    try {
      const resolvedId = this.resolveId(id);
      if (this._isTempId(resolvedId)) {
        this._removeFromLocalStorage(collectionName, id);
        await this._markAsDeleted(collectionName, resolvedId);
        await this._createTombstoneInLocal(collectionName, resolvedId);
        return { success: true, id: resolvedId, _skipped: true, _localOnly: true, _tombstone: true };
      }
      await this._markAsDeleted(collectionName, resolvedId);
      await this._createTombstoneInLocal(collectionName, resolvedId);
      if (!this._localStrategy) {
        this._removeFromLocalStorage(collectionName, resolvedId);
        this._removeFromLocalStorage(collectionName, id);
      }
      if (this.db && this.isOnline) {
        try {
          const docRef = this.getDocRef(collectionName, resolvedId);
          const snap = await getDoc(docRef);
          if (!snap.exists()) {
            if (collectionName === 'properties') {
              try { await deleteDoc(doc(this.db, 'public_properties', resolvedId)); } catch (_) {}
            }
            return { success: true, id: resolvedId, _alreadyDeleted: true, _syncStatus: 'synced', _tombstone: true };
          }
          await deleteDoc(docRef);
          if (collectionName === 'properties') {
            try {
              const publicRef = doc(this.db, 'public_properties', resolvedId);
              if ((await getDoc(publicRef)).exists()) await deleteDoc(publicRef);
            } catch (pubError) {}
          }
          return { success: true, id: resolvedId, _syncStatus: 'synced', _deleted: true, _tombstone: true };
        } catch (firestoreError) {
          try { await syncService.syncDelete(collectionName, resolvedId, this.companyId, this.userId); } catch (syncError) {}
          return { success: true, id: resolvedId, _syncStatus: 'pending', _deleted: true, _tombstone: true, _firestoreError: firestoreError.message };
        }
      }
      const syncId = await syncService.syncDelete(collectionName, resolvedId, this.companyId, this.userId);
      return { success: true, id: resolvedId, _syncId: syncId, _syncStatus: 'pending', _deleted: true, _tombstone: true };
    } catch (error) {
      console.error(`❌ Error eliminando ${collectionName}/${id}:`, error);
      throw error;
    }
  }

  // ======================================================
  // 🔧 UTILIDADES
  // ======================================================
  generateId() { return `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`; }
  _generateOfflineId() { return `offline_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`; }

  async verifyDocumentOwnership(collectionName, id) {
    try {
      const docRef = this.getDocRef(collectionName, id);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) throw new Error('Documento no encontrado');
      if (docSnap.data().companyId !== this.companyId) throw new Error(`Documento no pertenece a la empresa ${this.companyId}`);
      return true;
    } catch (error) { throw error; }
  }

  async getAll(collectionName) { return this.read(collectionName, {}); }
  getSyncStatus() { return syncService.getSyncStatus(); }
  async forceSyncNow() { return await syncService.processSyncQueue(); }
  getPendingChanges() { const s = syncService.getSyncStatus(); return { pending: s.pending, failed: s.failed, total: s.queueLength }; }

  async getDashboardStats() {
    try {
      const [clients, properties, tasks] = await Promise.all([this.getAll('clients'), this.getAll('properties'), this.getAll('tasks')]);
      return {
        activeProperties: properties.filter(p => ['Disponível', 'available'].includes(p.status)).length,
        activeClients: clients.filter(c => c.pipeline !== "Fechado" || c.status === "Ativo" || c.status === "active").length,
        pendingTasks: tasks.filter(t => ['pending', 'pendente'].includes(t.status)).length,
        totalProperties: properties.length, totalClients: clients.length, totalTasks: tasks.length,
        source: 'cloud', syncStatus: this.getSyncStatus()
      };
    } catch (error) { return { activeProperties: 0, activeClients: 0, pendingTasks: 0, totalProperties: 0, totalClients: 0, totalTasks: 0, source: 'cloud_error' }; }
  }

  async getTasksByClient(clientId) {
    try { return await this.read('tasks', { where: [{ field: 'clientId', operator: '==', value: clientId }] }); }
    catch (error) { return []; }
  }

  async getCalendarEventsByProperty(propertyId) {
    try { return await this.read('calendarEvents', { where: [{ field: 'propertyId', operator: '==', value: propertyId }] }); }
    catch (error) { return []; }
  }

  healthCheck() {
    const s = syncService.getSyncStatus();
    return {
      type: 'cloud', version: 'V14',
      features: {
        realTimeSync: '🔄 V14: Real-time Sync (onSnapshot - propiedades, clientes, tareas)',
        transactions: '🔒 V14: Transacciones seguras (runTransaction)',
        autoRepair: `🔧 V13: Auto-Repair (${this._autoRepairCount} reparaciones)`,
        antiDuplicate: '🔧 V13: Anti-Duplicate por título',
        orphanCleanup: '🔧 V13: Limpieza de huérfanos',
        raceConditionLock: '🔧 V13: Lock con timeout (5s stale release)'
      },
      online: this.isOnline, firebaseReady: !!this.db,
      userId: this.userId, companyId: this.companyId,
      autoRepairCount: this._autoRepairCount, activeLocks: this._autoRepairLocks.size,
      activeListeners: this._activeListeners.size,
      timestamp: new Date().toISOString()
    };
  }

  async cleanDuplicatedUsers() {
    try {
      const usersSnapshot = await getDocs(collection(this.db, 'users'));
      const usersByEmail = {}, usersToDelete = [];
      usersSnapshot.forEach(docSnap => {
        const u = docSnap.data();
        if (!usersByEmail[u.email]) usersByEmail[u.email] = [];
        usersByEmail[u.email].push({ id: docSnap.id, ...u });
      });
      Object.entries(usersByEmail).forEach(([, users]) => {
        if (users.length > 1) {
          const sorted = users.sort((a, b) => (a.createdAt?.toDate?.() || 0) - (b.createdAt?.toDate?.() || 0));
          sorted.slice(1).forEach(dup => { if (dup.id !== this.userId) usersToDelete.push(dup); });
        }
      });
      for (const user of usersToDelete) { try { await deleteDoc(doc(this.db, 'users', user.id)); } catch (e) {} }
      return { success: true, deletedCount: usersToDelete.length };
    } catch (error) { throw error; }
  }

  // ======================================================
  // 🗑️ DESTRUCTOR: Limpiar listeners
  // ======================================================
  destroy() {
    this.unsubscribeAll();
    console.log('🗑️ CloudStrategy V14 destruido — listeners liberados');
  }
}

export default CloudStrategy;